// eslint-disable-next-line
/// <reference path="../../tellent-mobile-shell/logic/global.d.ts" />
import { inject, InjectionToken } from '@angular/core';

export const isNativeMobileHr = window.isNativeMobileHr ?? false;

export const IS_NATIVE_MOBILE_HR = new InjectionToken<boolean>('IsNativeMobileHr');

export const provideIsNativeMobileHr = () => ({
  provide: IS_NATIVE_MOBILE_HR,
  useValue: isNativeMobileHr,
});

export const injectIsNativeMobileHr = () =>
  inject(IS_NATIVE_MOBILE_HR, { optional: true }) || false;

export const setupNativeMobileHr = async (config: { tellentAuthUrl: string }) => {
  await window.nativeMobileHrReady;
  window.nativeMobileHr.setAuthUrl(config);
};

export const fetchTokenFromNative = () => window.nativeMobileHr.fetchTokenFromNative();

export const getMobileInfo = () => window.nativeMobileHr.getMobileInfo();

export const openAuthBrowserInNativeApp = () => window.nativeMobileHr.openAuth();

export const switchOrgInNativeApp = (guid: string) => window.nativeMobileHr.switch(guid);

/**
 * Push notifications — events bridged from `tellent-mobile-shell-logic`.
 *
 * `tellent-client` imports these to subscribe to Capacitor-driven events
 * without taking a direct dependency on the shell library (and vice versa).
 * See Mobile architecture meeting notes (2026-04-30) for context.
 */
export const MOBILE_PUSH_EVENT = <const>{
  authReady: 'tellent-hr-mobile:authReady',
  registerDevice: 'tellent-hr-mobile:registerDevice',
  pushTokenReady: 'tellent-hr-mobile:pushTokenReady',
  pushNotificationReceived: 'tellent-hr-mobile:pushNotificationReceived',
  pushNotificationActionPerformed: 'tellent-hr-mobile:pushNotificationActionPerformed',
  beforeSignOut: 'tellent-hr-mobile:beforeSignOut',
  unregisterError: 'tellent-hr-mobile:unregisterError',
};

export type MobileDevicePlatform = 'ios' | 'android' | 'web';

export type MobileDeviceDescriptor = {
  platform: MobileDevicePlatform;
  app_bundle_id: string;
  app_env: 'debug' | 'staging' | 'rc' | 'production';
  device_uid: string;
  /**
   * APNS/FCM token if push permission was already granted at the time the
   * shell collected the descriptor; null otherwise. The web app sends this
   * with the initial POST so the device is registered with credentials in
   * one round-trip. Later token rotations come in via `pushTokenReady` and
   * are applied via PATCH.
   */
  token: string | null;
};

export type MobilePushTokenReady = {
  device_uid: string;
  token: string;
};

export type MobilePushNotificationReceived = {
  notification: {
    title?: string;
    body?: string;
    data?: Record<string, unknown>;
    [key: string]: unknown;
  };
};

export type MobilePushNotificationActionPerformed = {
  actionId: string;
  inputValue?: string;
  notification: MobilePushNotificationReceived['notification'];
};

export type MobilePushPermission = 'prompt' | 'prompt-with-rationale' | 'granted' | 'denied';

/**
 * Shows the OS push permission prompt (when still possible) and registers the
 * device on grant. The shell exposes this only on ios/android — on older shell
 * binaries (OTA skew) or outside the shell it resolves to 'denied'.
 */
export const requestPushNotificationsInNativeApp = (): Promise<{
  permission: MobilePushPermission;
}> =>
  window.nativeMobileHr.requestPushNotifications?.() ?? Promise.resolve({ permission: 'denied' });

/** Read-only permission lookup — never triggers the OS prompt. */
export const checkPushPermissionsInNativeApp = (): Promise<{
  permission: MobilePushPermission;
}> => window.nativeMobileHr.checkPushPermissions?.() ?? Promise.resolve({ permission: 'denied' });

/** Opens the OS notification settings screen for the app. */
export const openNotificationSettingsInNativeApp = (): Promise<void> =>
  window.nativeMobileHr.openNotificationSettings?.() ?? Promise.resolve();

export const dispatchAuthReady = () => {
  window.dispatchEvent(new CustomEvent(MOBILE_PUSH_EVENT.authReady));
};

/**
 * Sign-out helper that allows subscribers to run cleanup tasks (e.g. DELETE
 * the device registration on the TS API while auth is still valid) before
 * the native shell tears down credentials.
 */
export const signOutInNativeApp = async () => {
  window[<keyof typeof window>'KiwiHR']?.showSplashScreen();
  const cleanupPromises: Array<Promise<unknown>> = [];
  window.dispatchEvent(
    new CustomEvent(MOBILE_PUSH_EVENT.beforeSignOut, { detail: { cleanupPromises } }),
  );
  await Promise.allSettled(cleanupPromises);
  await window.nativeMobileHr.logoutInNativeApp();
};
